/**
 * Single source of truth for firm-level facts, navigation and CTAs.
 *
 * Anything marked PLACEHOLDER was not supplied in the brand document and
 * must be confirmed by the firm before launch.
 */

type Site = {
  name: string;
  shortName: string;
  initials: string;
  disciplines: readonly string[];
  /** The three-word service line set beneath the name in the logo lockup. */
  lockupLine: readonly string[];
  tagline: string;
  description: string;
  url: string;
  domain: string;
  affiliate: { name: string; country: string };
  /** Optional fields render only when populated — see PLACEHOLDER notes. */
  contact: { email: string; phone: string; location: string };
  social: { linkedin: string };
};

export const site: Site = {
  name: "Mutiu Adejumobi & Associates Ltd",
  shortName: "Mutiu Adejumobi & Associates",
  initials: "MA",
  disciplines: [
    "Chartered Accountants",
    "Auditors",
    "Tax Practitioners",
    "Business Advisors",
  ],
  lockupLine: ["Accounting", "Tax", "Advisory"],
  tagline: "Building Better Businesses Through Finance, Technology & Strategy",
  description:
    "A leading Nigerian firm of Chartered Accountants, Auditors, Tax Practitioners and Business Advisors — strengthening governance, improving financial performance and building sustainable growth.",
  url: "https://www.mutiuassociates.com",
  domain: "www.mutiuassociates.com",
  affiliate: {
    name: "Bukz Accounting Services Ltd",
    country: "United Kingdom",
  },
  contact: {
    email: "info@mutiuassociates.com", // PLACEHOLDER — confirm with the firm
    // Left empty on purpose: no number was supplied, and inventing one is worse
    // than omitting it. Set it here and it renders everywhere automatically.
    phone: "",
    location: "Lagos, Nigeria", // PLACEHOLDER — confirm office address
  },
  social: {
    linkedin: "", // PLACEHOLDER
  },
};

export type NavItem = {
  label: string;
  href: string;
  description?: string;
};

export const primaryNav: NavItem[] = [
  { label: "About", href: "/about", description: "Who we are and how we work" },
  {
    label: "Services",
    href: "/services",
    description: "Accounting, audit, tax and advisory",
  },
  {
    label: "Industries",
    href: "/industries",
    description: "Sector expertise across the economy",
  },
  {
    label: "Academy",
    href: "/academy",
    description: "Professional education and training",
  },
  {
    label: "Insights",
    href: "/insights",
    description: "Practical guidance from our team",
  },
  { label: "Contact", href: "/contact", description: "Start a conversation" },
];

/** The three calls to action used consistently across the site. */
export const ctas = {
  primary: { label: "Book a Consultation", href: "/contact?intent=consultation" },
  secondary: { label: "Request a Proposal", href: "/contact?intent=proposal" },
  tertiary: { label: "Speak to an Expert", href: "/contact?intent=expert" },
} as const;

export const differentiators = [
  {
    title: "Chartered expertise, commercial instinct",
    body: "Chartered professionals with real, practical commercial experience — technically rigorous, but always focused on the decision in front of you.",
  },
  {
    title: "Technology-driven by default",
    body: "Modern finance technology sits at the centre of how we work, from cloud accounting through to enterprise ERP and management dashboards.",
  },
  {
    title: "Cross-border capability",
    body: `International reach through our United Kingdom affiliate, ${site.affiliate.name}, combined with deep expertise in Nigeria's regulatory environment.`,
  },
  {
    title: "Independent and client-centred",
    body: "Independent, ethical advice. We tell you what we think, not what is easiest to say.",
  },
  {
    title: "Industry-specific knowledge",
    body: "Sector insight that shortens the ramp-up on every engagement and sharpens the recommendations that follow.",
  },
  {
    title: "Beyond compliance",
    body: "Compliance is the starting point, not the destination. We work as long-term strategic partners to organisations built to last.",
  },
  {
    title: "Responsive and relationship-focused",
    body: "You work with the people who did the work, and you get an answer while the question still matters.",
  },
  {
    title: "International standards, local insight",
    body: "International best practice applied with a working knowledge of the Nigerian regulatory environment it has to survive.",
  },
];
